import React, { useState, useEffect, useCallback } from 'react';
import { 
  Button, 
  Space, 
  Tag, 
  Typography, 
  message, 
  Modal, 
  Form, 
  InputNumber, 
  Switch, 
  Input,
  Checkbox,
  Popconfirm,
  Spin,
  Row,
  Col,
  Badge,
  Tooltip,
} from 'antd';
import { 
  ReloadOutlined, 
  PlusOutlined, 
  EditOutlined, 
  SaveOutlined, 
  CloseOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  CrownOutlined,
  RocketOutlined,
  ThunderboltOutlined,
  StarOutlined,
  GoldOutlined,
  InfoCircleOutlined,
  CheckOutlined,
} from '@ant-design/icons';
import { apiClient, API_ENDPOINTS } from '../../../utils/api';
import styled, { keyframes, css } from 'styled-components';

const COLORS = {
  bgPrimary: '#0D1117',
  bgSecondary: '#161B22',
  bgTertiary: '#21262D',
  border: '#30363D',
  textPrimary: '#E6EDF3',
  textSecondary: '#8B949E',
  textMuted: '#6E7681',
  accent: '#58A6FF',
  accentGreen: '#3FB950',
  accentPurple: '#A371F7',
  accentOrange: '#F0883E',
  accentRed: '#F85149',
  accentCyan: '#39D353',
  gradientStart: '#238636',
  gradientEnd: '#2EA043',
  cardBg: 'linear-gradient(145deg, #161B22 0%, #0D1117 100%)',
  glassBg: 'rgba(22, 27, 34, 0.8)',
};

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
`;

const { Title, Text } = Typography;
const { TextArea } = Input;

const PageContainer = styled.div`
  padding: 0;
  color: ${COLORS.textPrimary};
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 0 8px;
  }
`;

const HeaderContainer = styled.div`
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  h2 {
    margin-bottom: 0 !important;
    font-size: 28px !important;
    font-weight: 700 !important;
    background: linear-gradient(135deg, ${COLORS.textPrimary} 0%, ${COLORS.accent} 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  
  @media (max-width: 768px) {
    margin-bottom: 20px;
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    h2 {
      font-size: 22px !important;
    }
  }
`;

const PlansGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

const PlanCard = styled.div<{ $isPopular?: boolean; $isEditing?: boolean; $isInactive?: boolean }>`
  background: ${COLORS.cardBg};
  border-radius: 16px;
  padding: 28px;
  border: 2px solid ${props => props.$isPopular ? COLORS.accent : COLORS.border};
  position: relative;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  
  ${props => props.$isInactive && css`
    opacity: 0.6;
    border-color: ${COLORS.border};
    filter: grayscale(30%);
  `}
  
  ${props => props.$isEditing && css`
    border-color: ${COLORS.accentOrange};
    box-shadow: 0 0 30px rgba(240, 136, 62, 0.2);
  `}
  
  ${props => props.$isPopular && !props.$isInactive && css`
    box-shadow: 0 0 40px rgba(88, 166, 255, 0.15);
    animation: ${float} 3s ease-in-out infinite;
  `}
  
  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
    border-color: ${props => props.$isEditing ? COLORS.accentOrange : COLORS.accent};
  }
`;

const PopularBadge = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, ${COLORS.accent} 0%, ${COLORS.accentPurple} 100%);
  padding: 6px 20px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 4px 15px rgba(88, 166, 255, 0.4);
`;

const PlanHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const PlanIcon = styled.div<{ $tier: string }>`
  width: 60px;
  height: 60px;
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 16px;
  font-size: 28px;
  
  ${props => {
    switch (props.$tier) {
      case 'FREE':
        return `background: linear-gradient(135deg, #30363D 0%, #21262D 100%); color: ${COLORS.textSecondary};`;
      case 'STARTER':
        return `background: linear-gradient(135deg, #238636 0%, #2EA043 100%); color: white;`;
      case 'PROFESSIONAL':
        return `background: linear-gradient(135deg, ${COLORS.accent} 0%, #1F6FEB 100%); color: white;`;
      case 'ENTERPRISE':
        return `background: linear-gradient(135deg, ${COLORS.accentPurple} 0%, #8957E5 100%); color: white;`;
      default:
        return `background: linear-gradient(135deg, ${COLORS.accentOrange} 0%, #DB6D28 100%); color: white;`;
    }
  }}
`;

const PlanName = styled.h3`
  font-size: 22px;
  font-weight: 700;
  color: ${COLORS.textPrimary};
  margin: 0 0 4px 0;
`;

const PlanDescription = styled.p`
  font-size: 14px;
  color: ${COLORS.textSecondary};
  margin: 0;
  line-height: 1.5;
`;

const PriceSection = styled.div`
  text-align: center;
  margin-bottom: 28px;
  padding: 20px;
  background: rgba(88, 166, 255, 0.05);
  border-radius: 12px;
`;

const PriceAmount = styled.div`
  font-size: 48px;
  font-weight: 800;
  color: ${COLORS.textPrimary};
  line-height: 1;
  
  span {
    font-size: 18px;
    font-weight: 500;
    color: ${COLORS.textSecondary};
  }
`;

const PricePeriod = styled.div`
  font-size: 14px;
  color: ${COLORS.textSecondary};
  margin-top: 4px;
`;

const LimitsSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  color: ${COLORS.textSecondary};
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${COLORS.border};
  }
`;

const LimitRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 0;
  border-bottom: 1px solid ${COLORS.border};
  
  &:last-child {
    border-bottom: none;
  }
`;

const LimitLabel = styled.span`
  font-size: 14px;
  color: ${COLORS.textSecondary};
`;

const LimitValue = styled.span<{ $isUnlimited?: boolean }>`
  font-size: 14px;
  font-weight: 600;
  color: ${props => props.$isUnlimited ? COLORS.accentGreen : COLORS.textPrimary};
`;

const BlockchainsSection = styled.div`
  margin-bottom: 24px;
`;

const BlockchainTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const BlockchainTag = styled(Tag)<{ $supported: boolean }>`
  padding: 4px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  border: none;
  
  ${props => props.$supported 
    ? `background: rgba(63, 185, 80, 0.15); color: ${COLORS.accentGreen};`
    : `background: rgba(139, 148, 158, 0.1); color: ${COLORS.textSecondary}; text-decoration: line-through;`
  }
`;

const CardActions = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 20px;
  padding-top: 20px;
  border-top: 1px solid ${COLORS.border};
`;

const ActionButton = styled(Button)`
  flex: 1;
`;

const StatusBadge = styled.div<{ $active: boolean }>`
  position: absolute;
  top: 16px;
  right: 16px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${props => props.$active ? COLORS.accentGreen : COLORS.accentRed};
  box-shadow: 0 0 10px ${props => props.$active ? COLORS.accentGreen : COLORS.accentRed};
`;

const ComparisonTable = styled.div`
  background: ${COLORS.bgSecondary};
  border-radius: 16px;
  border: 1px solid ${COLORS.border};
  overflow: hidden;
  margin-top: 32px;
`;

const TableHeader = styled.div`
  background: ${COLORS.bgTertiary};
  padding: 16px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid ${COLORS.border};
`;

const TableTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${COLORS.textPrimary};
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const TableScrollWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;
  
  th, td {
    padding: 14px 16px;
    text-align: left;
    border-bottom: 1px solid ${COLORS.border};
  }
  
  th {
    background: ${COLORS.bgTertiary};
    color: ${COLORS.textSecondary};
    font-weight: 600;
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    position: sticky;
    top: 0;
    z-index: 1;
  }
  
  td {
    color: ${COLORS.textPrimary};
    font-size: 14px;
  }
  
  tr:hover td {
    background: rgba(88, 166, 255, 0.05);
  }
  
  .plan-name {
    font-weight: 600;
  }
  
  .editable-cell {
    cursor: pointer;
    padding: 8px;
    border-radius: 6px;
    transition: background 0.2s;
    
    &:hover {
      background: rgba(88, 166, 255, 0.1);
    }
  }
`;

const EditInput = styled(InputNumber)`
  width: 100%;
  
  .ant-input-number-input {
    background: ${COLORS.bgPrimary};
    color: ${COLORS.textPrimary};
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-content {
    background: ${COLORS.bgSecondary} !important;
    border: 1px solid ${COLORS.border} !important;
    border-radius: 16px !important;
  }
  
  .ant-modal-header {
    background: ${COLORS.bgSecondary} !important;
    border-bottom: 1px solid ${COLORS.border} !important;
    border-radius: 16px 16px 0 0 !important;
    padding: 20px 24px !important;
    
    .ant-modal-title {
      color: ${COLORS.textPrimary} !important;
      font-size: 20px !important;
      font-weight: 600 !important;
    }
  }
  
  .ant-modal-body {
    padding: 24px !important;
    color: ${COLORS.textPrimary} !important;
  }
  
  .ant-modal-footer {
    background: ${COLORS.bgSecondary} !important;
    border-top: 1px solid ${COLORS.border} !important;
    padding: 16px 24px !important;
    border-radius: 0 0 16px 16px !important;
  }
  
  .ant-form-item-label > label {
    color: ${COLORS.textPrimary} !important;
    font-weight: 500 !important;
  }
  
  .ant-input, .ant-input-number, .ant-input-textarea textarea {
    background: ${COLORS.bgPrimary} !important;
    border-color: ${COLORS.border} !important;
    color: ${COLORS.textPrimary} !important;
    
    &:hover, &:focus {
      border-color: ${COLORS.accent} !important;
    }
  }
  
  .ant-input-number {
    .ant-input-number-input {
      background: ${COLORS.bgPrimary} !important;
      color: ${COLORS.textPrimary} !important;
    }
    
    .ant-input-number-handler-wrap {
      background: ${COLORS.bgSecondary} !important;
      border-left: 1px solid ${COLORS.border} !important;
      opacity: 1 !important;
      
      .ant-input-number-handler {
        border-color: ${COLORS.border} !important;
        
        &:hover {
          background: ${COLORS.accent} !important;
          
          .ant-input-number-handler-up-inner,
          .ant-input-number-handler-down-inner {
            color: white !important;
          }
        }
        
        .ant-input-number-handler-up-inner,
        .ant-input-number-handler-down-inner {
          color: ${COLORS.textSecondary} !important;
          font-size: 10px !important;
        }
      }
      
      .ant-input-number-handler-up {
        border-bottom: 1px solid ${COLORS.border} !important;
      }
    }
    
    &:hover .ant-input-number-handler-wrap {
      opacity: 1 !important;
    }
  }
  
  .ant-checkbox-wrapper {
    color: ${COLORS.textPrimary} !important;
  }
`;

const FormSection = styled.div`
  background: ${COLORS.bgTertiary};
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 20px;
  
  .section-title {
    font-size: 14px;
    font-weight: 600;
    color: ${COLORS.accent};
    margin-bottom: 16px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
`;

const CheckboxGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

interface SubscriptionPlan {
  id: string;
  name: string;
  displayName: string;
  monthlyPrice: number;
  walletLimit: number;
  staffLimit: number;
  blockchainLimit: number;
  currencyLimit: number;
  contractLimit: number;
  nativeTokenLimit: number;
  supportsEvm: boolean;
  supportsBitcoin: boolean;
  supportsSolana: boolean;
  supportsTron: boolean;
  supportsCustomChains: boolean;
  features: Record<string, any> | null;
  isActive: boolean;
  isCustom: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
}

const getPlanIcon = (name: string) => {
  const upperName = name.toUpperCase();
  if (upperName.includes('FREE')) return <StarOutlined />;
  if (upperName.includes('STARTER')) return <RocketOutlined />;
  if (upperName.includes('PROFESSIONAL') || upperName.includes('PRO')) return <ThunderboltOutlined />;
  if (upperName.includes('ENTERPRISE')) return <GoldOutlined />;
  return <CrownOutlined />;
};

const getPlanTier = (name: string) => {
  const upperName = name.toUpperCase();
  if (upperName.includes('FREE')) return 'FREE';
  if (upperName.includes('STARTER')) return 'STARTER';
  if (upperName.includes('PROFESSIONAL') || upperName.includes('PRO')) return 'PROFESSIONAL';
  if (upperName.includes('ENTERPRISE')) return 'ENTERPRISE';
  return 'CUSTOM';
};

const formatLimit = (limit: number, isCustomPlan: boolean = false) => {
  if (isCustomPlan) return 'Per merchant';
  if (limit === -1) return 'Unlimited';
  if (limit === 0) return '0';
  return limit.toLocaleString();
};

const isCustomPlan = (planName: string) => {
  return planName?.toUpperCase() === 'CUSTOM';
};

const SubscriptionPlans: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
  const [editedPlans, setEditedPlans] = useState<Record<string, Partial<SubscriptionPlan>>>({});
  const [hasChanges, setHasChanges] = useState(false);
  const [form] = Form.useForm();

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(
        API_ENDPOINTS.ADMIN_ALL_SUBSCRIPTION_PLANS || '/api/Administrator/subscription-plans/all'
      );
      
      if (response.data?.status === 'success' && response.data?.data) {
        setPlans(response.data.data);
      }
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to fetch subscription plans');
      console.error('Error fetching subscription plans:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleEditPlan = (plan: SubscriptionPlan) => {
    setSelectedPlan(plan);
    form.setFieldsValue({
      ...plan,
      monthlyPrice: parseFloat(plan.monthlyPrice.toString()),
    });
    setEditModalVisible(true);
  };

  const handleCreatePlan = () => {
    setSelectedPlan(null);
    form.resetFields();
    form.setFieldsValue({
      walletLimit: 100,
      staffLimit: 5,
      blockchainLimit: 2,
      currencyLimit: 5,
      contractLimit: 10,
      nativeTokenLimit: 2,
      supportsEvm: true,
      supportsBitcoin: false,
      supportsSolana: false,
      supportsTron: false,
      supportsCustomChains: false,
      isActive: true,
      isCustom: false,
    });
    setCreateModalVisible(true);
  };

  const handleSavePlan = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);

      if (selectedPlan) {
        // Update existing plan
        const endpoint = API_ENDPOINTS.ADMIN_UPDATE_SUBSCRIPTION_PLAN.replace('{planId}', selectedPlan.id);
        await apiClient.put(endpoint, values);
        message.success('Subscription plan updated successfully');
      } else {
        // Create new plan
        await apiClient.post(API_ENDPOINTS.ADMIN_CREATE_SUBSCRIPTION_PLAN, values);
        message.success('Subscription plan created successfully');
      }

      setEditModalVisible(false);
      setCreateModalVisible(false);
      fetchPlans();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save subscription plan');
    } finally {
      setSaving(false);
    }
  };

  const handleDeletePlan = async (planId: string) => {
    try {
      const endpoint = API_ENDPOINTS.ADMIN_DELETE_SUBSCRIPTION_PLAN.replace('{planId}', planId);
      await apiClient.delete(endpoint);
      message.success('Subscription plan deactivated successfully');
      fetchPlans();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete subscription plan');
    }
  };

  const handleActivatePlan = async (planId: string) => {
    try {
      const endpoint = API_ENDPOINTS.ADMIN_UPDATE_SUBSCRIPTION_PLAN.replace('{planId}', planId);
      await apiClient.put(endpoint, { isActive: true });
      message.success('Subscription plan activated successfully');
      fetchPlans();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to activate subscription plan');
    }
  };

  const handleTableCellChange = (planId: string, field: string, value: any) => {
    setEditedPlans(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        [field]: value,
      },
    }));
    setHasChanges(true);
  };

  const handleBulkSave = async () => {
    if (Object.keys(editedPlans).length === 0) {
      message.info('No changes to save');
      return;
    }

    try {
      setSaving(true);
      const updates = Object.entries(editedPlans).map(([id, data]) => ({
        id,
        data,
      }));

      await apiClient.put(API_ENDPOINTS.ADMIN_BULK_UPDATE_SUBSCRIPTION_PLANS, { updates });
      message.success(`Successfully updated ${updates.length} plan(s)`);
      setEditedPlans({});
      setHasChanges(false);
      fetchPlans();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelChanges = () => {
    setEditedPlans({});
    setHasChanges(false);
  };

  const getPlanValue = (plan: SubscriptionPlan, field: keyof SubscriptionPlan) => {
    if (editedPlans[plan.id] && editedPlans[plan.id][field] !== undefined) {
      return editedPlans[plan.id][field];
    }
    return plan[field];
  };

  const renderPlanCards = () => {
    const activePlans = plans.filter(p => p.isActive);
    const middleIndex = Math.floor(activePlans.length / 2);
    
    return (
    <PlansGrid>
      {plans.map((plan) => {
        const activeIndex = activePlans.findIndex(p => p.id === plan.id);
        const isPopular = activeIndex === middleIndex && plan.isActive;
        
        return (
        <PlanCard 
          key={plan.id} 
          $isPopular={isPopular}
          $isInactive={!plan.isActive}
        >
          {isPopular && (
            <PopularBadge>Most Popular</PopularBadge>
          )}
          {!plan.isActive && (
            <div style={{
              position: 'absolute',
              top: 16,
              left: 16,
              background: COLORS.accentRed,
              color: '#fff',
              padding: '4px 10px',
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: '0.5px',
            }}>
              INACTIVE
            </div>
          )}
          <StatusBadge $active={plan.isActive} />
          
          <PlanHeader>
            <PlanIcon $tier={getPlanTier(plan.name)}>
              {getPlanIcon(plan.name)}
            </PlanIcon>
            <PlanName>{plan.displayName}</PlanName>
            <PlanDescription>{plan.description || 'Perfect for your needs'}</PlanDescription>
          </PlanHeader>

          <PriceSection>
            <PriceAmount>
              ${parseFloat(plan.monthlyPrice.toString()).toFixed(0)}
              <span>/mo</span>
            </PriceAmount>
            <PricePeriod>Billed monthly</PricePeriod>
          </PriceSection>

          <LimitsSection>
            <SectionTitle>Resource Limits</SectionTitle>
            {isCustomPlan(plan.name) ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '16px 0',
                color: COLORS.textSecondary,
                fontSize: '13px',
              }}>
                <Tag color="purple" style={{ marginBottom: 8 }}>Fully Customizable</Tag>
                <div>All limits are configured per merchant</div>
              </div>
            ) : (
              <>
                <LimitRow>
                  <LimitLabel>Wallets</LimitLabel>
                  <LimitValue $isUnlimited={plan.walletLimit === -1}>
                    {formatLimit(plan.walletLimit)}
                  </LimitValue>
                </LimitRow>
                <LimitRow>
                  <LimitLabel>Staff Members</LimitLabel>
                  <LimitValue $isUnlimited={plan.staffLimit === -1}>
                    {formatLimit(plan.staffLimit)}
                  </LimitValue>
                </LimitRow>
                <LimitRow>
                  <LimitLabel>Blockchains</LimitLabel>
                  <LimitValue $isUnlimited={plan.blockchainLimit === -1}>
                    {formatLimit(plan.blockchainLimit)}
                  </LimitValue>
                </LimitRow>
                <LimitRow>
                  <LimitLabel>Currencies</LimitLabel>
                  <LimitValue $isUnlimited={plan.currencyLimit === -1}>
                    {formatLimit(plan.currencyLimit)}
                  </LimitValue>
                </LimitRow>
                <LimitRow>
                  <LimitLabel>Contracts</LimitLabel>
                  <LimitValue $isUnlimited={plan.contractLimit === -1}>
                    {formatLimit(plan.contractLimit)}
                  </LimitValue>
                </LimitRow>
                <LimitRow>
                  <LimitLabel>Native Tokens</LimitLabel>
                  <LimitValue $isUnlimited={plan.nativeTokenLimit === -1}>
                    {formatLimit(plan.nativeTokenLimit)}
                  </LimitValue>
                </LimitRow>
              </>
            )}
          </LimitsSection>

          <BlockchainsSection>
            <SectionTitle>Supported Blockchains</SectionTitle>
            {isCustomPlan(plan.name) ? (
              <div style={{ 
                textAlign: 'center', 
                padding: '8px 0',
                color: COLORS.textSecondary,
                fontSize: '13px',
              }}>
                <Tag color="purple">All chains available</Tag>
              </div>
            ) : (
              <BlockchainTags>
                <BlockchainTag $supported={plan.supportsEvm}>EVM</BlockchainTag>
                <BlockchainTag $supported={plan.supportsBitcoin}>Bitcoin</BlockchainTag>
                <BlockchainTag $supported={plan.supportsSolana}>Solana</BlockchainTag>
                <BlockchainTag $supported={plan.supportsTron}>TRON</BlockchainTag>
                <BlockchainTag $supported={plan.supportsCustomChains}>Custom</BlockchainTag>
              </BlockchainTags>
            )}
          </BlockchainsSection>

          <CardActions>
            {isCustomPlan(plan.name) ? (
              <Tooltip title="Custom plan limits are set per merchant in their company settings">
                <ActionButton 
                  type="default"
                  icon={<InfoCircleOutlined />}
                  style={{ 
                    background: 'transparent', 
                    borderColor: COLORS.accentPurple,
                    color: COLORS.accentPurple,
                  }}
                >
                  Per-Merchant Config
                </ActionButton>
              </Tooltip>
            ) : (
              <>
                <ActionButton 
                  type="primary"
                  icon={<EditOutlined />}
                  onClick={() => handleEditPlan(plan)}
                  style={{ background: COLORS.accent, borderColor: COLORS.accent }}
                >
                  Edit
                </ActionButton>
                {plan.isActive ? (
                  <Popconfirm
                    title="Deactivate Plan? This will deactivate the plan. Active subscriptions won't be affected."
                    onConfirm={() => handleDeletePlan(plan.id)}
                    okText="Deactivate"
                    cancelText="Cancel"
                  >
                    <ActionButton 
                      danger
                      icon={<DeleteOutlined />}
                    >
                      Deactivate
                    </ActionButton>
                  </Popconfirm>
                ) : (
                  <Popconfirm
                    title="Activate Plan? This will make the plan available for new subscriptions."
                    onConfirm={() => handleActivatePlan(plan.id)}
                    okText="Activate"
                    cancelText="Cancel"
                  >
                    <ActionButton 
                      icon={<CheckOutlined />}
                      style={{ background: COLORS.accentGreen, borderColor: COLORS.accentGreen, color: '#fff' }}
                    >
                      Activate
                    </ActionButton>
                  </Popconfirm>
                )}
              </>
            )}
          </CardActions>
        </PlanCard>
      );
      })}
    </PlansGrid>
  );
  };

  const renderComparisonTable = () => (
    <ComparisonTable>
      <TableHeader>
        <TableTitle>
          <EditOutlined /> Comparison Editor
          {hasChanges && (
            <Badge 
              count="Unsaved Changes" 
              style={{ 
                backgroundColor: COLORS.accentOrange, 
                marginLeft: 12,
                fontSize: 11,
              }} 
            />
          )}
        </TableTitle>
        <Space>
          {hasChanges && (
            <>
              <Button 
                icon={<CloseOutlined />} 
                onClick={handleCancelChanges}
              >
                Cancel
              </Button>
              <Button 
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleBulkSave}
                loading={saving}
                style={{ background: COLORS.accentGreen, borderColor: COLORS.accentGreen }}
              >
                Save All Changes
              </Button>
            </>
          )}
        </Space>
      </TableHeader>
      <TableScrollWrapper>
        <StyledTable>
          <thead>
            <tr>
              <th>Plan</th>
              <th>Price ($/mo)</th>
              <th>Wallets</th>
              <th>Staff</th>
              <th>Blockchains</th>
              <th>Currencies</th>
              <th>Contracts</th>
              <th>Native Tokens</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => {
              const isCustom = isCustomPlan(plan.name);
              return (
              <tr key={plan.id} style={isCustom ? { background: 'rgba(163, 113, 247, 0.05)' } : {}}>
                <td className="plan-name">
                  <Space>
                    <span style={{ color: isCustom ? COLORS.accentPurple : COLORS.accent }}>{getPlanIcon(plan.name)}</span>
                    {plan.displayName}
                    {isCustom && <Tag color="purple">Per Merchant</Tag>}
                  </Space>
                </td>
                <td>
                  {isCustom ? (
                    <Tag color="purple" style={{ margin: 0 }}>Custom</Tag>
                  ) : (
                    <EditInput
                      value={getPlanValue(plan, 'monthlyPrice') as number}
                      onChange={(value) => handleTableCellChange(plan.id, 'monthlyPrice', value)}
                      min={0}
                      precision={2}
                      prefix="$"
                      size="small"
                    />
                  )}
                </td>
                <td>
                  {isCustom ? (
                    <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>Per merchant</span>
                  ) : (
                    <EditInput
                      value={getPlanValue(plan, 'walletLimit') as number}
                      onChange={(value) => handleTableCellChange(plan.id, 'walletLimit', value)}
                      min={-1}
                      size="small"
                      placeholder="-1 for unlimited"
                    />
                  )}
                </td>
                <td>
                  {isCustom ? (
                    <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>Per merchant</span>
                  ) : (
                    <EditInput
                      value={getPlanValue(plan, 'staffLimit') as number}
                      onChange={(value) => handleTableCellChange(plan.id, 'staffLimit', value)}
                      min={-1}
                      size="small"
                    />
                  )}
                </td>
                <td>
                  {isCustom ? (
                    <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>Per merchant</span>
                  ) : (
                    <EditInput
                      value={getPlanValue(plan, 'blockchainLimit') as number}
                      onChange={(value) => handleTableCellChange(plan.id, 'blockchainLimit', value)}
                      min={-1}
                      size="small"
                    />
                  )}
                </td>
                <td>
                  {isCustom ? (
                    <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>Per merchant</span>
                  ) : (
                    <EditInput
                      value={getPlanValue(plan, 'currencyLimit') as number}
                      onChange={(value) => handleTableCellChange(plan.id, 'currencyLimit', value)}
                      min={-1}
                      size="small"
                    />
                  )}
                </td>
                <td>
                  {isCustom ? (
                    <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>Per merchant</span>
                  ) : (
                    <EditInput
                      value={getPlanValue(plan, 'contractLimit') as number}
                      onChange={(value) => handleTableCellChange(plan.id, 'contractLimit', value)}
                      min={-1}
                      size="small"
                    />
                  )}
                </td>
                <td>
                  {isCustom ? (
                    <span style={{ color: COLORS.textSecondary, fontSize: 12 }}>Per merchant</span>
                  ) : (
                    <EditInput
                      value={getPlanValue(plan, 'nativeTokenLimit') as number}
                      onChange={(value) => handleTableCellChange(plan.id, 'nativeTokenLimit', value)}
                      min={-1}
                      size="small"
                    />
                  )}
                </td>
                <td>
                  <Switch
                    checked={getPlanValue(plan, 'isActive') as boolean}
                    onChange={(checked) => handleTableCellChange(plan.id, 'isActive', checked)}
                    checkedChildren={<CheckCircleOutlined />}
                    size="small"
                  />
                </td>
              </tr>
            );
            })}
          </tbody>
        </StyledTable>
      </TableScrollWrapper>
    </ComparisonTable>
  );

  const renderEditModal = () => (
    <StyledModal
      title={selectedPlan ? `Edit: ${selectedPlan.displayName}` : 'Create Subscription Plan'}
      open={editModalVisible || createModalVisible}
      onCancel={() => {
        setEditModalVisible(false);
        setCreateModalVisible(false);
        setSelectedPlan(null);
      }}
      footer={[
        <Button key="cancel" onClick={() => {
          setEditModalVisible(false);
          setCreateModalVisible(false);
          setSelectedPlan(null);
        }}>
          Cancel
        </Button>,
        <Button 
          key="save" 
          type="primary" 
          onClick={handleSavePlan}
          loading={saving}
          icon={<SaveOutlined />}
          style={{ background: COLORS.accent, borderColor: COLORS.accent }}
        >
          {selectedPlan ? 'Update Plan' : 'Create Plan'}
        </Button>,
      ]}
      width={800}
    >
      <Form form={form} layout="vertical">
        <FormSection>
          <div className="section-title">Basic Information</div>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="Internal Name"
                rules={[{ required: true, message: 'Please enter plan name' }]}
              >
                <Input placeholder="e.g., PROFESSIONAL" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="displayName"
                label="Display Name"
                rules={[{ required: true, message: 'Please enter display name' }]}
              >
                <Input placeholder="e.g., Professional Plan" />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="monthlyPrice"
                label="Monthly Price (USD)"
                rules={[{ required: true, message: 'Please enter price' }]}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={0} 
                  precision={2}
                  prefix="$"
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="description"
                label="Description"
              >
                <TextArea rows={2} placeholder="Plan description..." />
              </Form.Item>
            </Col>
          </Row>
        </FormSection>

        <FormSection>
          <div className="section-title">Resource Limits</div>
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column',
            gap: 8, 
            marginBottom: 20,
            padding: '16px',
            background: 'rgba(34, 211, 238, 0.15)',
            borderRadius: 12,
            border: `2px solid ${COLORS.accent}50`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <InfoCircleOutlined style={{ color: COLORS.accent, fontSize: 18 }} />
              <Text style={{ color: COLORS.textPrimary, fontSize: 14, fontWeight: 600 }}>
                How to set limits:
              </Text>
            </div>
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'auto 1fr', 
              gap: '8px 16px',
              marginLeft: 26,
            }}>
              <Tag color="cyan" style={{ fontWeight: 700, fontSize: 14 }}>-1</Tag>
              <Text style={{ color: COLORS.textSecondary, fontSize: 13 }}>
                = <strong style={{ color: COLORS.accentGreen }}>UNLIMITED</strong> (no restrictions)
              </Text>
              <Tag color="blue" style={{ fontWeight: 700, fontSize: 14 }}>0+</Tag>
              <Text style={{ color: COLORS.textSecondary, fontSize: 13 }}>
                = Specific limit (e.g., 100 = max 100 items)
              </Text>
            </div>
          </div>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item 
                name="walletLimit" 
                label={<span style={{ color: COLORS.textPrimary }}>Wallet Limit</span>}
                rules={[{ required: true, message: 'Required' }]}
                help={<span style={{ color: COLORS.textMuted, fontSize: 11 }}>-1 = unlimited</span>}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={-1} 
                  step={1}
                  precision={0}
                  placeholder="Enter -1 for unlimited"
                  parser={(value) => {
                    const num = parseInt(value || '0', 10);
                    if (num < -1) return -1;
                    if (num > -1 && num < 0) return 0;
                    return num;
                  }}
                  controls={{
                    upIcon: <span style={{ fontSize: 10 }}>▲</span>,
                    downIcon: <span style={{ fontSize: 10 }}>▼</span>,
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name="staffLimit" 
                label={<span style={{ color: COLORS.textPrimary }}>Staff Limit</span>}
                rules={[{ required: true, message: 'Required' }]}
                help={<span style={{ color: COLORS.textMuted, fontSize: 11 }}>-1 = unlimited</span>}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={-1} 
                  step={1}
                  precision={0}
                  placeholder="Enter -1 for unlimited"
                  parser={(value) => {
                    const num = parseInt(value || '0', 10);
                    if (num < -1) return -1;
                    if (num > -1 && num < 0) return 0;
                    return num;
                  }}
                  controls={{
                    upIcon: <span style={{ fontSize: 10 }}>▲</span>,
                    downIcon: <span style={{ fontSize: 10 }}>▼</span>,
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name="blockchainLimit" 
                label={<span style={{ color: COLORS.textPrimary }}>Blockchain Limit</span>}
                rules={[{ required: true, message: 'Required' }]}
                help={<span style={{ color: COLORS.textMuted, fontSize: 11 }}>-1 = unlimited</span>}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={-1} 
                  step={1}
                  precision={0}
                  placeholder="Enter -1 for unlimited"
                  parser={(value) => {
                    const num = parseInt(value || '0', 10);
                    if (num < -1) return -1;
                    if (num > -1 && num < 0) return 0;
                    return num;
                  }}
                  controls={{
                    upIcon: <span style={{ fontSize: 10 }}>▲</span>,
                    downIcon: <span style={{ fontSize: 10 }}>▼</span>,
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item 
                name="currencyLimit" 
                label={<span style={{ color: COLORS.textPrimary }}>Currency Limit</span>}
                rules={[{ required: true, message: 'Required' }]}
                help={<span style={{ color: COLORS.textMuted, fontSize: 11 }}>-1 = unlimited</span>}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={-1} 
                  step={1}
                  precision={0}
                  placeholder="Enter -1 for unlimited"
                  parser={(value) => {
                    const num = parseInt(value || '0', 10);
                    if (num < -1) return -1;
                    if (num > -1 && num < 0) return 0;
                    return num;
                  }}
                  controls={{
                    upIcon: <span style={{ fontSize: 10 }}>▲</span>,
                    downIcon: <span style={{ fontSize: 10 }}>▼</span>,
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name="contractLimit" 
                label={<span style={{ color: COLORS.textPrimary }}>Contract Limit</span>}
                rules={[{ required: true, message: 'Required' }]}
                help={<span style={{ color: COLORS.textMuted, fontSize: 11 }}>-1 = unlimited</span>}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={-1} 
                  step={1}
                  precision={0}
                  placeholder="Enter -1 for unlimited"
                  parser={(value) => {
                    const num = parseInt(value || '0', 10);
                    if (num < -1) return -1;
                    if (num > -1 && num < 0) return 0;
                    return num;
                  }}
                  controls={{
                    upIcon: <span style={{ fontSize: 10 }}>▲</span>,
                    downIcon: <span style={{ fontSize: 10 }}>▼</span>,
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item 
                name="nativeTokenLimit" 
                label={<span style={{ color: COLORS.textPrimary }}>Native Token Limit</span>}
                rules={[{ required: true, message: 'Required' }]}
                help={<span style={{ color: COLORS.textMuted, fontSize: 11 }}>-1 = unlimited</span>}
              >
                <InputNumber 
                  style={{ width: '100%' }} 
                  min={-1} 
                  step={1}
                  precision={0}
                  placeholder="Enter -1 for unlimited"
                  parser={(value) => {
                    const num = parseInt(value || '0', 10);
                    if (num < -1) return -1;
                    if (num > -1 && num < 0) return 0;
                    return num;
                  }}
                  controls={{
                    upIcon: <span style={{ fontSize: 10 }}>▲</span>,
                    downIcon: <span style={{ fontSize: 10 }}>▼</span>,
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
        </FormSection>

        <FormSection>
          <div className="section-title">Supported Blockchains</div>
          <CheckboxGrid>
            <Form.Item name="supportsEvm" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>
                <Space>
                  <Tag color="blue">EVM</Tag>
                  Ethereum, BSC, Polygon, etc.
                </Space>
              </Checkbox>
            </Form.Item>
            <Form.Item name="supportsBitcoin" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>
                <Space>
                  <Tag color="orange">Bitcoin</Tag>
                  BTC Network
                </Space>
              </Checkbox>
            </Form.Item>
            <Form.Item name="supportsSolana" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>
                <Space>
                  <Tag color="purple">Solana</Tag>
                  SOL Network
                </Space>
              </Checkbox>
            </Form.Item>
            <Form.Item name="supportsTron" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>
                <Space>
                  <Tag color="red">TRON</Tag>
                  TRX Network
                </Space>
              </Checkbox>
            </Form.Item>
            <Form.Item name="supportsCustomChains" valuePropName="checked" style={{ marginBottom: 8 }}>
              <Checkbox>
                <Space>
                  <Tag color="green">Custom</Tag>
                  Custom blockchain integration
                </Space>
              </Checkbox>
            </Form.Item>
          </CheckboxGrid>
        </FormSection>

        <FormSection>
          <div className="section-title">Plan Settings</div>
          <Row gutter={16}>
            <Col span={8}>
              <Form.Item name="isActive" valuePropName="checked">
                <Checkbox>
                  <Space>
                    <CheckCircleOutlined style={{ color: COLORS.accentGreen }} />
                    Active Plan
                  </Space>
                </Checkbox>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name="isCustom" valuePropName="checked">
                <Checkbox>
                  <Space>
                    <CrownOutlined style={{ color: COLORS.accentPurple }} />
                    Custom Plan
                  </Space>
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        </FormSection>
      </Form>
    </StyledModal>
  );

  return (
    <PageContainer>
      <HeaderContainer>
        <Title level={2} style={{ color: COLORS.textPrimary, margin: 0 }}>
          Subscription Plans
        </Title>
        <Space>
          <Button
            icon={<ReloadOutlined />}
            onClick={fetchPlans}
            loading={loading}
            style={{ 
              color: COLORS.textPrimary, 
              borderColor: COLORS.border,
              background: COLORS.bgTertiary,
            }}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleCreatePlan}
            style={{ background: COLORS.accentGreen, borderColor: COLORS.accentGreen }}
          >
            Create Plan
          </Button>
        </Space>
      </HeaderContainer>

      <Spin spinning={loading}>
        {renderPlanCards()}
        {renderComparisonTable()}
      </Spin>

      {renderEditModal()}
    </PageContainer>
  );
};

export default SubscriptionPlans;
